/** Fill Megaventory Product/product insert payloads from SKU patterns (parity with Appsmith `product.js` categorization). */

import { getDeviceFamily } from "@integrator-src/manufacturing/configuration-sku-family";

export type MegaventoryProductInsertDraft = Record<string, unknown>;

export type MegaventoryInsertCategorization = "specific" | "cots_prefix" | "fallback";

type CotsPrefixRule = {
  prefix: string;
  productDescription: string;
  productCategoryName?: string;
};

/** Longest prefixes first so `HD-MISC-` wins over shorter matches. */
const COTS_PREFIX_RULES: readonly CotsPrefixRule[] = [
  { prefix: "HD-MISC-", productDescription: "Test Point" },
  { prefix: "ANT-", productDescription: "Cell Antenna" },
  { prefix: "BAT-", productDescription: "Battery", productCategoryName: "Battery" },
  { prefix: "CON-", productDescription: "Connector" },
  { prefix: "CS-", productDescription: "Cap" },
  { prefix: "CT-", productDescription: "Cap" },
  { prefix: "DS-", productDescription: "Diode" },
  { prefix: "FB-", productDescription: "Ferrite Bead" },
  { prefix: "FS-", productDescription: "Fuse" },
  { prefix: "FT-", productDescription: "Fuse" },
  { prefix: "GPS-", productDescription: "GPS Antenna" },
  { prefix: "ICS-", productDescription: "IC" },
  { prefix: "ID-", productDescription: "Inductor" },
  { prefix: "LD-", productDescription: "LED" },
  { prefix: "MG-", productDescription: "Magnet" },
  { prefix: "PCB-", productDescription: "PCB" },
  { prefix: "Q-", productDescription: "Transistor" },
  { prefix: "RS-", productDescription: "Res" },
  { prefix: "SDH-", productDescription: "Component" },
  { prefix: "SHM-", productDescription: "RF Shield" },
  { prefix: "SPK-", productDescription: "Speaker" },
  { prefix: "XS-", productDescription: "Crystal" },
].sort((left, right) => right.prefix.length - left.prefix.length);

const SPECIFIC_SKU_PREFIXES = [
  "88-104-",
  "88-403-",
  "15-",
  "88-012-",
  "88-013-",
  "88-005-",
  "88-107-",
  "88-018-",
  "88-011-",
] as const;

/**
 * SKU strings harvested from `[SKU]` snippets in Appsmith-style error lists.
 */
export function parseSkuBrackets(messages: readonly string[]): string[] {
  const skuRegex = /\[([^\]]+)\]/;
  const unique = new Set<string>();
  for (const raw of messages) {
    const line = typeof raw === "string" ? raw.trim() : "";
    if (!line) continue;
    const match = line.match(skuRegex);
    if (match && match[1]) {
      unique.add(match[1].trim());
    }
  }
  return [...unique];
}

function resolveCotsPrefixRule(sku: string): CotsPrefixRule | null {
  for (const rule of COTS_PREFIX_RULES) {
    if (sku.startsWith(rule.prefix)) {
      return rule;
    }
  }
  return null;
}

function hasSpecificSkuRule(sku: string): boolean {
  const deviceFamily = getDeviceFamily(sku);
  if (deviceFamily === "patrolmanPlus" || deviceFamily === "patrolman" || deviceFamily === "recon") {
    return true;
  }
  return SPECIFIC_SKU_PREFIXES.some((prefix) => sku.startsWith(prefix));
}

export function classifyMegaventoryInsertSku(skuRaw: string): MegaventoryInsertCategorization {
  const sku = skuRaw.trim();
  if (hasSpecificSkuRule(sku)) {
    return "specific";
  }
  if (resolveCotsPrefixRule(sku) != null) {
    return "cots_prefix";
  }
  return "fallback";
}

export function megaventoryInsertProductDescription(draft: MegaventoryProductInsertDraft): string {
  const value = draft.ProductDescription;
  return typeof value === "string" ? value.trim() : "";
}

export function isMegaventoryInsertDraftReady(draft: MegaventoryProductInsertDraft): boolean {
  return megaventoryInsertProductDescription(draft).length > 0;
}

function applyBuyFromSupplierDefaults(
  draft: MegaventoryProductInsertDraft,
  productDescription: string,
  productCategoryName: string,
): void {
  draft.ProductCategoryName = productCategoryName;
  draft.ProductDescription = productDescription;
  draft.ProductVersion = "(Variant not set yet)";
  draft.IsSellable = false;
  draft.IsPurchaseable = true;
  draft.ProductType = "BuyFromSupplier";
  draft.WeightUnitName = "Each";
}

function applyCotsPrefixRule(draft: MegaventoryProductInsertDraft, rule: CotsPrefixRule): void {
  applyBuyFromSupplierDefaults(
    draft,
    rule.productDescription,
    rule.productCategoryName ?? "Circuitry",
  );
}

function applyMegaventoryInsertFallbackDefaults(draft: MegaventoryProductInsertDraft, sku: string): void {
  applyBuyFromSupplierDefaults(draft, sku, "Component");
}

function finalizeMegaventoryInsertDraft(draft: MegaventoryProductInsertDraft): MegaventoryProductInsertDraft {
  draft.mvProductCategory = {
    ProductCategoryName: draft.ProductCategoryName,
  };
  draft.mvWeightUnit = { WeightUnitName: draft.WeightUnitName };
  return draft;
}

/**
 * Populate one Megaventory insert row for `Product/ProductUpdate` with `mvRecordAction` Insert,
 * shaped like the Appsmith `create_products` datasource body `mvProduct` field.
 */
export function buildMegaventoryInsertMvProductDraft(skuRaw: string): MegaventoryProductInsertDraft {
  const sku = skuRaw.trim();
  const draft: MegaventoryProductInsertDraft = {
    ProductSKU: sku,
    ProductCategoryName: "",
    ProductDescription: "",
    ProductVersion: "",
    IsSellable: false,
    IsPurchaseable: false,
    ProductType: "",
    WeightUnitName: "",
    mvProductCategory: { ProductCategoryName: "" },
    mvWeightUnit: { WeightUnitName: "" },
  };

  const deviceFamily = getDeviceFamily(sku);

  if (sku.startsWith("88-104-")) {
    const parts = sku.split("-");
    const variant = `(${parts[parts.length - 1]})`;
    draft.ProductCategoryName = "Label";
    draft.ProductDescription = "Device Label";
    draft.ProductVersion = `${variant}-24`;
    draft.IsSellable = false;
    draft.IsPurchaseable = true;
    draft.ProductType = "BuyFromSupplier";
    draft.WeightUnitName = "Each";
  } else if (deviceFamily === "patrolmanPlus") {
    draft.ProductCategoryName = "Product";
    draft.ProductDescription = "Overhead Wireless FCI";
    draft.ProductVersion = "(Variant not set yet)";
    draft.IsSellable = false;
    draft.IsPurchaseable = false;
    draft.ProductType = "ManufactureFromWorkOrder";
    draft.WeightUnitName = "Each";
  } else if (deviceFamily === "patrolman") {
    draft.ProductCategoryName = "Product";
    draft.ProductDescription = "Overhead FCI";
    draft.ProductVersion = "(Variant not set yet)";
    draft.IsSellable = false;
    draft.IsPurchaseable = false;
    draft.ProductType = "ManufactureFromWorkOrder";
    draft.WeightUnitName = "Each";
  } else if (sku.startsWith("88-403-")) {
    const parts = sku.split("-");
    const variant = `(${parts[parts.length - 2]})`;
    draft.ProductCategoryName = "Label";
    draft.ProductDescription = "Box Label";
    draft.ProductVersion = `${variant}-24`;
    draft.IsSellable = false;
    draft.IsPurchaseable = true;
    draft.ProductType = "BuyFromSupplier";
    draft.WeightUnitName = "Each";
  } else if (sku.startsWith("15-")) {
    draft.ProductCategoryName = "Component";
    draft.ProductDescription = "PCBA";
    draft.ProductVersion = "(Variant not set yet)";
    draft.IsSellable = false;
    draft.IsPurchaseable = false;
    draft.ProductType = "ManufactureFromWorkOrder";
    draft.WeightUnitName = "Each";
  } else if (deviceFamily === "recon") {
    draft.ProductCategoryName = "Product";
    draft.ProductDescription = "Underground FCI";
    draft.ProductVersion = "(Variant not set yet)";
    draft.IsSellable = true;
    draft.IsPurchaseable = false;
    draft.ProductType = "ManufactureFromWorkOrder";
    draft.WeightUnitName = "Each";
  } else if (sku.startsWith("88-012-")) {
    const parts = sku.split("-");
    const variant = `(${parts[parts.length - 1]})`;
    draft.ProductCategoryName = "Label";
    draft.ProductDescription = "Device Label";
    draft.ProductVersion = variant;
    draft.IsSellable = false;
    draft.IsPurchaseable = true;
    draft.ProductType = "BuyFromSupplier";
    draft.WeightUnitName = "Each";
  } else if (sku.startsWith("88-013-")) {
    const parts = sku.split("-");
    const variant = `(${parts[parts.length - 2]})`;
    draft.ProductCategoryName = "Label";
    draft.ProductDescription = "Box Label";
    draft.ProductVersion = variant;
    draft.IsSellable = false;
    draft.IsPurchaseable = true;
    draft.ProductType = "BuyFromSupplier";
    draft.WeightUnitName = "Each";
  } else if (sku.startsWith("88-005-")) {
    const parts = sku.split("-");
    const variant = `(${parts[parts.length - 2]}; ${parts[parts.length - 1]})`;
    draft.ProductCategoryName = "Component";
    draft.ProductDescription = "Fiber Cable";
    draft.ProductVersion = variant;
    draft.IsSellable = false;
    draft.IsPurchaseable = true;
    draft.ProductType = "BuyFromSupplierOrManufactureFromWorkOrder";
    draft.WeightUnitName = "Each";
  } else if (sku.startsWith("88-107-")) {
    const tail = sku.slice("88-107-".length);
    draft.ProductCategoryName = "Packaging";
    draft.ProductDescription = "Polybag";
    draft.ProductVersion = tail.length > 0 ? `(${tail.replace(/X/gi, "x")})` : "(Variant not set yet)";
    draft.IsSellable = true;
    draft.IsPurchaseable = true;
    draft.ProductType = "BuyFromSupplier";
    draft.WeightUnitName = "Each";
  } else if (sku.startsWith("88-018-")) {
    const parts = sku.split("-");
    const phaseCode = parts[2] ?? "";
    const phaseLabel =
      phaseCode === "PH" ? "Three Phase" : phaseCode === "OC" ? "Single Phase" : phaseCode;
    const versionParts: string[] = [phaseLabel];
    for (let index = 3; index < parts.length; index += 1) {
      const segment = parts[index] ?? "";
      if (/^\d+$/.test(segment)) {
        versionParts.push(`${segment} Foot Fiber Optic Cable`);
      } else if (segment === "HEX") {
        versionParts.push("Hex Head");
      } else if (segment === "SF") {
        versionParts.push("Snow Flag");
      } else if (segment.length > 0) {
        versionParts.push(segment);
      }
    }
    draft.ProductCategoryName = "Label";
    draft.ProductDescription = "Accessory Label";
    draft.ProductVersion = `(${versionParts.join(", ")})`;
    draft.IsSellable = true;
    draft.IsPurchaseable = true;
    draft.ProductType = "BuyFromSupplier";
    draft.WeightUnitName = "Each";
  } else if (sku.startsWith("88-011-")) {
    const parts = sku.split("-");
    const variant = `(${parts[parts.length - 2]}; Included Accessory)`;
    draft.ProductCategoryName = "Label";
    draft.ProductDescription = "Box Label";
    draft.ProductVersion = variant;
    draft.IsSellable = false;
    draft.IsPurchaseable = true;
    draft.ProductType = "BuyFromSupplierOrManufactureFromWorkOrder";
    draft.WeightUnitName = "Each";
  }

  if (!megaventoryInsertProductDescription(draft)) {
    const cotsRule = resolveCotsPrefixRule(sku);
    if (cotsRule != null) {
      applyCotsPrefixRule(draft, cotsRule);
    } else {
      applyMegaventoryInsertFallbackDefaults(draft, sku);
    }
  }

  return finalizeMegaventoryInsertDraft(draft);
}

/** @deprecated Use {@link classifyMegaventoryInsertSku} — kept for existing tests. */
export function categorizeKnownPrefix(sku: string): boolean {
  return classifyMegaventoryInsertSku(sku) === "specific";
}
